import { Component, computed, inject, input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HeaderNavbarLinks, ListDropdownService } from '@vueling-ng/vy-ds/src/lib/components/composed/header';
import { SidePanelService } from '@vueling-ng/vy-ds/src/lib/components/globals/side-panel';
import { CurrencyService } from 'app/services/internal/currency/currency.service';
import { LanguageService } from 'app/services/internal/language/language.service';
import { UserInfo } from 'app/shared/state/models/app-state.model';
import { internalRoutes } from 'environments/common/internal-routes';
import { languages } from 'environments/common/internationalization';
import { environment } from 'environments/environment';
import { AccountManagementComponent } from './components/account-management/account-management.component';
import { AccountManagementData } from './components/account-management/models/account-management.model';
import {
  LangCurrencySelectorComponent,
  LanguageCurrencyData
} from './components/lang-currency-selector/lang-currency-selector.component';
import {
  frenchInfoHelpMenu,
  headerData,
  headerElementNavbarLinks,
  headerNavbarAgencyLinkItem,
  infoHelpMenu
} from './components/models/header.model';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html'
})
export class HeaderComponent {
  public language = input.required<string | undefined>();
  public userInfo = input.required<UserInfo | undefined>();
  public isTransparent = input<boolean>(false);

  private readonly _sidePanelService: SidePanelService = inject(SidePanelService);
  private readonly _translateService: TranslateService = inject(TranslateService);
  private readonly _languageService: LanguageService = inject(LanguageService);
  private readonly _currencyService = inject(CurrencyService);
  private readonly _listDropdownService = inject(ListDropdownService);

  public readonly ticketsUrl: string = environment.smartBaseUrl;
  public readonly linkLogo: string = internalRoutes.Home;

  public currentCurrency = computed(() => {
    this.updateCurrentCurrency();
    return this._currencyService.currentCurrency();
  });

  public headerNavbarLinks = computed(() => {
    const userType = this.userInfo()?.userType?.toLocaleLowerCase() ?? 'default';
    const language = this._languageService.currentLanguage();

    let links: HeaderNavbarLinks;

    if (userType === 'corporate' || userType === 'default') {
      links = headerElementNavbarLinks;
    } else {
      headerElementNavbarLinks.leftLinks = headerNavbarAgencyLinkItem;
      links = headerElementNavbarLinks;
    }

    if (language === languages.french) {
      links.rightLinks[1] = frenchInfoHelpMenu;
    } else {
      links.rightLinks[1] = infoHelpMenu;
    }

    return links;
  });

  public getLanguageSelected(): string {
    return this.language()?.slice(0, 2) ?? this._translateService.defaultLang;
  }

  public getLogoName(): string {
    return `header_component.logo.${this.userInfo()?.userType?.toLocaleLowerCase() ?? 'default'}`;
  }

  public onLangButtonClicked(): void {
    const data: LanguageCurrencyData = {
      languages: this._translateService.langs.map((lang) => {
        return { value: lang, label: `common.languages.${lang}`, labelSuffix: lang.split('-')[0] };
      }),
      currencies: this._currencyService.getAllCurrency()
    };

    this._sidePanelService.open(LangCurrencySelectorComponent, {
      title: headerData.langCurrencySelectorSidePanel.label,
      customData: data
    });
  }

  public onLoggedButtonClicked(): void {
    this._sidePanelService.open(AccountManagementComponent, {
      title: headerData.loggedUserMenu.label,
      customData: {
        language: this.language(),
        userName: this.userInfo()?.userName,
        email: this.userInfo()?.email
      } as AccountManagementData
    });
  }

  private updateCurrentCurrency(): void {
    this._listDropdownService.sendCurrency(this._currencyService.getParserCurrentCurrency());
  }
}
